import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import * as data from '../../../assets/json/countries.json'

/**
 * Representa los datos relativos a un país
 */
interface CountryData {
  /** prefijo telefónico del país. Ej: '+34'*/
  [key: string]: {
    /** Código ISO2 del país. Ej: 'ES'*/
    [key: string]: {
      /** Código ISO3 del país. Ej: 'ESP'*/
      "ISO-3": string;
      /** Nombre del país. Ej: 'España'*/
      "Country": string;
    };
  };
}

/**
 * Representa las regiones de Cuba
 */
export interface Region {
  /** ID de la region */
  id: number;
  /** ID numérico del país */
  country_id: number;
  /** Nomnbre de la región*/
  name: string;
  /** Código asociado a la región*/
  code: string;
}

/**
 * Representa las zonas de Cuba
 */
export interface Zone {
  /** Nombre de la zona*/
  name: string;
  /** Lista de áreas de la zona*/
  areas: string[];
}

/**
 * Representa a las áreas de Cuba
 */
export interface Area {
  /** Nombre del área */
  name: string;
  /** ID único del área*/
  short_code: string;
  /** Lista de municipios @see Municipality */
  municipalities: Municipality[];
}

/**
 * Representa a los municipios de Cuba
 */
export interface Municipality {
  /** Nombre del municipio */
  name: string;
  /** Código único del municipio */
  code: string;
  /** Código único del punto de recogida de dinero */
  payment_point_code: string;
}

/**
 * Representa las zonas de Cuba
 */
export interface Zones {
  /** Lista de zonas @see Zone*/
  zones: Zone[];
  /** Lista de áreas @see Area*/
  areas: Area[];
}

/**
 * Objeto que engloba todas las regiones de un país
 */
export interface RegionsByCountry {
  /** Código ISO3 del país */
  [countryCode: string]:
    /** Regiones del país @see Region */
    Region[] |
    /** Zonas del país @see Zones */
    Zones;
}

/**
* Componente reutilizable que genera formularios de forma dinámica en función del objeto de entrada.
* Primer uso pensado para la creación delos formularios dinámicos necesarios para las transferencias internacionales debido a la diversidad de formularios necesarios por los diferentres productos de "Revolut"
*/
@Component({
  selector: 'app-beneficiary-form',
  templateUrl: './beneficiary-form.component.html',
})
export class BeneficiaryFormComponent implements OnChanges {
  /** Propiedad que representa las regiones del país */
  @Input() regions!: RegionsByCountry;
  /** Propiedad que representa el importe a enviar */
  @Input() amount: number = 0;
  /** Propiedad que representa el producto selecionado en el paso anterior del formulario */
  @Input() product: any = {};
  /** Propiedad que representa el país selecionado en el paso anterior del formulario */
  @Input() selectedCountry: string = "";
  /** Propiedad que representa una lista de países válidos */
  @Input() filteredCountries: any[] = [];
  /** Propiedad que representa una lista de patrones válidos para la validación del formulario */
  @Input() fixedTextPatterns: any[] = [];

  /** Propiedad que representa el form generado dinámicamente */
  @Input() _formBuilder: FormBuilder = inject(FormBuilder);


  beneficiary_fields_form: FormGroup;
  account_types_form: FormGroup;
  countryinfo: { countryCode: string, country: string } | null = null;
  selRegion: Region[] = [];
  selZone: Zones | null = null;

  /** Evento que emite al componente padre el formulario generado dinámicamente */
  @Output() messageEvent = new EventEmitter<{ key: string, value: FormGroup }>();

  zones: Zone[] = [];
  areas: Area[] = [];

  filteredAreas: Area[] = [];
  filteredMunicipalities: Municipality[] = [];

  constructor( ) {
    this.account_types_form = this._formBuilder.group({ });
    this.beneficiary_fields_form = this._formBuilder.group({ });
  }

  /**
   * Método para limpiar strings.
   * @param value - String a limpiar
   */
  replaceSpaces(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ /g, '_');
  }

  /**
   * Método del ciclo de vida que se ejecuta cuando se produce el cambio de producto y es necesario recrear el formulario.
   * @param changes - Nuevo producto
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['product']) {

      if (this.selectedCountry !== 'CUB') {
        this.selRegion = this.regions[this.selectedCountry] as Region[];
      } else {
        this.selZone = this.regions[this.selectedCountry] as Zones;
        this.zones = this.selZone.zones;
        this.areas = this.selZone.areas;
      }

      if (this.product.beneficiary_fields?.length > 0) {
        this.countryinfo = this.findCountryByISO3iso3(this.selectedCountry);
        this.product.beneficiary_fields.forEach((field: { regex: string | RegExp; name: string; }) => {
          const patternRegex = new RegExp(field.regex, 'u')
          this.beneficiary_fields_form.addControl(
            field.name,
            this._formBuilder.control('', [Validators.pattern(patternRegex), Validators.required])
          );
        });

        if (this.beneficiary_fields_form.contains('residenceCountryISO')) {
          this.beneficiary_fields_form.patchValue({ residenceCountryISO: this.selectedCountry });
        }
        
        this.messageEvent.emit({ key: 'beneficiary_fields', value: this.beneficiary_fields_form })
      }
    
      if (this.product.account_types?.length > 0) {
        // Iteramos sobre cada 'accounttype' en 'account_types'
        this.product.account_types.forEach((accounttype: { id: string, fields: { name: string, regex: string, validation: string }[] }) => {
          // Iteramos sobre cada 'field' en 'fields' de cada 'accounttype'
          accounttype.fields.forEach((field: { name: string, regex: string, validation: string }) => {
            const patternRegex = new RegExp(field.regex, 'u');

            // Añadimos un control por cada 'field'
            this.account_types_form.addControl(
              field.name,
              this._formBuilder.control('', [Validators.pattern(patternRegex), Validators.required])
            );
          });
        });

        // Emitimos el evento una vez que hemos añadido todos los controles
        this.messageEvent.emit({ key: 'account_types', value: this.account_types_form });
      }

    }
  }

  /**
   * Método del ciclo de vida que se ejecuta al inicializar el componente.
   */
  ngOnInit() {
    this.beneficiary_fields_form.valueChanges.subscribe(() => {
      this.checkIfAllFieldsFilled('beneficiary_fields', this.beneficiary_fields_form);
    });
    this.account_types_form.valueChanges.subscribe(() => {
      this.checkIfAllFieldsFilled('account_types', this.account_types_form);
    });

    this.account_types_form.get('zone')?.valueChanges.subscribe(zoneName => {
      const zone = this.zones.find(z => this.replaceSpaces(z.name) === zoneName);
      this.filteredAreas = this.areas.filter(a => zone?.areas.includes(a.name));

      // Reset dependientes
      this.account_types_form.get('area')?.reset();
      this.account_types_form.get('municipality')?.reset();
      this.filteredMunicipalities = [];
    });

    this.account_types_form.get('area')?.valueChanges.subscribe(areaName => {
      const area = this.areas.find(a => this.replaceSpaces(a.name) === areaName);
      this.filteredMunicipalities = area?.municipalities || [];

      // Reset municipio
      this.account_types_form.get('municipality')?.reset();
    });

  }

  /**
   * Método para comprobar que el formualrio está completado.
   * @param key - input a comprobar
   * @param form - formulario a copmprobar
   *
   * @returns El estado de la validación: true o false
   */
  checkIfAllFieldsFilled(key: string, form: FormGroup) {
    const allFieldsFilled = Object.keys(form.controls).every(field => {
      const control = form.get(field);
      return control?.value && control?.valid;  // Asegurarse de que el campo no esté vacío y sea válido
    });

    // Si todos los campos están llenos, emite el evento
    if (allFieldsFilled) {
      this.messageEvent.emit({ key: key, value: form });
    }
  }

  /**
   * Método para comprobar que el formualrio está completado.
   * @param iso3 - código de país del que recuperar la información completa
   * @param form - formulario a copmprobar
   *
   * @returns objeto JSON con el código ISO2 y el nombre del país
   */
  findCountryByISO3iso3(iso3: string): { countryCode: string, country: string } | null {
    const countries_info: CountryData = data;

    for (const key in countries_info) {
      for (const countryCode in countries_info[key]) {
        if (countries_info[key][countryCode]["ISO-3"] === iso3) {
          return { 'countryCode':countryCode.toLowerCase(), country: countries_info[key][countryCode]["Country"] };
        }
      }
    }
    return null; // Si no se encuentra el ISO-3
  }

  /**
   * Método para sacar las opciones seleccionables por el usuario y genera selects.
   * @param pattern - patrón con las opciones 
   * 
   * @returns Lista de valores para crear las opciones del select
   */
  extractOptionsFromPattern(pattern: string): string[] {
    const regex = /\(([^)]+)\)/;  // Buscar dentro de los paréntesis
    const match = pattern.match(regex);
    if (match) {
      // Split de las opciones dentro de los paréntesis
      return match[1].split('|');
    }
    return [];
  }

}
